import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { User, Lock, Trash2, Save, Camera, Loader2 } from "lucide-react";

const SettingsPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const API = process.env.REACT_APP_BACKEND_URL;
  const fileInputRef = useRef(null);

  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
  });

  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profileImage, setProfileImage] = useState(user?.profile_image || null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5MB");
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${API}/api/user/profile-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setProfileImage(data.url);
        toast.success("Photo de profil mise à jour");
        window.location.reload();
      } else {
        toast.error(data.detail || "Erreur lors de l'upload");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/user/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(t("settings.profileUpdated"));
        window.location.reload();
      } else {
        toast.error(data.detail || "Erreur");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t("settings.passwordMismatch"));
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error(t("settings.passwordTooShort"));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/user/password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(t("settings.passwordChanged"));
        setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
      } else {
        toast.error(data.detail || "Erreur");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const res = await fetch(`${API}/api/user/account`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(t("settings.accountDeleted"));
        logout();
        navigate("/");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Erreur");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  // Hide profile image upload for admin
  const showProfileImageUpload = user.role !== "admin";

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="settings-page">
      <div className="max-w-2xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground">{t("settings.title")}</h1>
            <p className="text-muted-foreground mt-2">{t("settings.subtitle")}</p>
          </div>

          {/* Profile Image Section - Only for regular users */}
          {showProfileImageUpload && (
            <Card data-testid="profile-image-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5" />
                  Photo de profil
                </CardTitle>
                <CardDescription>Ajoutez ou modifiez votre photo de profil</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-muted flex items-center justify-center border-2 border-border">
                      {profileImage ? (
                        <img 
                          src={profileImage.startsWith('/api') ? `${API}${profileImage}` : profileImage}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="h-10 w-10 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      data-testid="upload-profile-image-btn"
                    >
                      {uploadingImage ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Upload...
                        </>
                      ) : (
                        <>
                          <Camera className="h-4 w-4 mr-2" />
                          Choisir une photo
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">JPG, PNG ou WebP. Max 5MB.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Profile Section */}
          <Card data-testid="profile-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t("settings.personalInfo")}
              </CardTitle>
              <CardDescription>{t("settings.personalInfoDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div>
                  <Label htmlFor="full_name">{t("settings.fullName")}</Label>
                  <Input
                    id="full_name"
                    data-testid="input-full-name"
                    value={profileData.full_name}
                    onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">{t("settings.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" disabled={loading} data-testid="save-profile-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {t("settings.saveChanges")}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Password Section */}
          <Card data-testid="password-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                {t("settings.changePassword")}
              </CardTitle>
              <CardDescription>{t("settings.changePasswordDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="current_password">{t("settings.currentPassword")}</Label>
                  <Input
                    id="current_password"
                    type="password"
                    data-testid="input-current-password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="new_password">{t("settings.newPassword")}</Label>
                  <Input
                    id="new_password"
                    type="password"
                    data-testid="input-new-password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="confirm_password">{t("settings.confirmPassword")}</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    data-testid="input-confirm-password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" disabled={loading} data-testid="change-password-btn">
                  <Lock className="h-4 w-4 mr-2" />
                  {t("settings.updatePassword")}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Delete Account Section */}
          <Card className="border-destructive/50" data-testid="delete-account-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                {t("settings.dangerZone")}
              </CardTitle>
              <CardDescription>{t("settings.dangerZoneDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" data-testid="delete-account-btn">
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t("settings.deleteAccount")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("settings.confirmDelete")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("settings.confirmDeleteDesc")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      className="bg-destructive hover:bg-destructive/90"
                      data-testid="confirm-delete-btn"
                    >
                      {t("settings.deleteAccount")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default SettingsPage;
